import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api } from "../../api/client";
import { qk } from "../../api/queries";
import type { ContextVersion } from "../../api/types";
import { useDialogs } from "../../components/Dialogs";
import { Markdown } from "../../components/Markdown";
import { MarkdownEditor } from "../../components/MarkdownEditor";
import { RelativeTime } from "../../components/RelativeTime";
import { ErrorBox, Spinner } from "../../components/Spinner";
import { cx } from "../../lib/format";

export function ContextSettings({ pid }: { pid: string }) {
  const qc = useQueryClient();
  const { toast } = useDialogs();
  const current = useQuery({ queryKey: qk.context(pid), queryFn: () => api.context(pid) });
  const versions = useQuery({ queryKey: qk.contextVersions(pid), queryFn: () => api.contextVersions(pid) });
  const [body, setBody] = useState("");
  const [viewing, setViewing] = useState<ContextVersion | null>(null);

  useEffect(() => {
    if (current.data !== undefined) setBody(current.data?.body ?? "");
  }, [current.data]);

  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.putContext(pid, body),
    onSuccess: (v) => {
      qc.invalidateQueries({ queryKey: qk.context(pid) });
      qc.invalidateQueries({ queryKey: qk.contextVersions(pid) });
      toast(`Saved as version ${v.version}`, "info");
    },
  });

  if (current.isLoading) return <Spinner />;
  if (current.error) return <ErrorBox error={current.error} />;
  const dirty = body !== (current.data?.body ?? "");

  return (
    <div className="settings-split">
      <div className="settings-main">
        <p className="muted">
          Instructions and reference material given to every agent on every task in this project. Each save creates a
          new version; every agent run records the version it used. Repo files like <code>CLAUDE.md</code> /{" "}
          <code>AGENTS.md</code> are still read by the agents.
        </p>
        <MarkdownEditor value={body} onChange={setBody} rows={22} onSubmitShortcut={() => dirty && save.mutate()} />
        {save.error && <div className="error-box">{save.error.message}</div>}
        <div className="row-actions row-actions-start">
          <button className="btn btn-primary" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? "Saving…" : "Save new version"}
          </button>
          {dirty && (
            <button className="btn" onClick={() => setBody(current.data?.body ?? "")}>
              Discard changes
            </button>
          )}
          {current.data && <span className="muted small">Current: v{current.data.version}</span>}
        </div>
      </div>
      <aside className="settings-aside">
        <h3 className="section-label">Version history</h3>
        {versions.isLoading && <Spinner />}
        <ul className="version-list">
          {versions.data?.map((v) => (
            <li key={v.id}>
              <button
                className={cx("version-item", viewing?.id === v.id && "active")}
                onClick={() => setViewing(viewing?.id === v.id ? null : v)}
              >
                <strong>v{v.version}</strong>
                <span className="muted small">
                  {v.created_by_name} · <RelativeTime iso={v.created_at} />
                </span>
              </button>
              {viewing?.id === v.id && (
                <div className="version-preview">
                  <Markdown>{v.body}</Markdown>
                  <button className="btn btn-sm" onClick={() => setBody(v.body)}>
                    Load into editor
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      </aside>
    </div>
  );
}
